import React, { useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
  Paper,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import { auth } from "../../../../_services";

const ResponsiveCalendarDay = ({
  day,
  dayIndex,
  shiftSel,
  dayName,
  allUserName,
  availabilitySel,
  jwt,
  t,
  onClickUser,
  onClickShiftRequest,
  avoidSelection,
  expanded,
  onToggleExpand,
  targetShiftId,
  // Props per turni vacanti
  isVacantSelectMode = false,
  vacantRequests = [],
  onCancelVacantRequest,
  loggedUserId,
  myEligibleRoleIds = [],
}) => {
  const scrollRef = useRef(null);
  const userId = auth.getUserId(jwt);
  const effectiveUserId = loggedUserId || userId;

  const dayNameShort =
    dayName.find((q) => q.id === day.anaDayId)?.name?.substring(0, 3) || "";
  const dayLabel = `${dayNameShort} ${day.day}`;

  const isControlled = typeof expanded === "boolean";

  const partecipatesInDay = day.shifts.some((shift) =>
    shift.users.some((user) => user.anaUserId === effectiveUserId)
  );

  // LOGICA DI SCROLL AUTOMATICO
  useEffect(() => {
    const hasTargetShift = day.shifts.some(
      (s) => String(s.shiftId) === String(targetShiftId)
    );

    if (hasTargetShift && scrollRef.current && targetShiftId) {
      const timer = setTimeout(() => {
        scrollRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [targetShiftId]);

  // Verifica se l'utente ha una richiesta vacante pendente
  const hasPendingVacantRequest = (slotDay, anaShiftId, anaUserRoleId) => {
    return (vacantRequests || []).some(
      (req) =>
        req.anaUserId === effectiveUserId &&
        req.daySel === slotDay &&
        req.anaShiftId === anaShiftId &&
        req.anaUserRoleId === anaUserRoleId &&
        !req.confirmedAt &&
        !req.refusedAt
    );
  };

  // Verifica se lo slot è vacante
  const isSlotVacant = (user) => {
    return user.anaUserId === 0 || user.anaUserId === null;
  };

  // Verifica se l'utente è qualificato per il ruolo
  const isUserQualified = (anaUserRoleId) => {
    return myEligibleRoleIds?.includes(anaUserRoleId) || false;
  };

  return (
    <Accordion
      key={`accordion_day_${dayIndex}`}
      ref={scrollRef}
      sx={{
        mb: 1,
        borderLeft: partecipatesInDay ? "4px solid #1976d2" : "none",
      }}
      {...(isControlled
        ? { expanded, onChange: () => onToggleExpand(dayIndex) }
        : {})}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" gap={1}>
          {partecipatesInDay && (
            <Tooltip title={t("youHaveAShiftHere")} placement="top">
              <FiberManualRecordIcon sx={{ fontSize: 10, color: "green" }} />
            </Tooltip>
          )}
          <Typography variant="body1" fontWeight="bold" color="textPrimary">
            {dayLabel}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {day.shifts.map((shift, shiftIdx) => {
          const shiftInfo = shiftSel.find((s) => s.id === shift.anaShiftId);
          const shiftLabel = `${shiftInfo?.timeFrom?.substring(0, 5)} - ${
            shiftInfo?.timeTo?.substring(0, 5)
          }`;

          const isTarget =
            targetShiftId &&
            String(shift.shiftId) === String(targetShiftId) &&
            shift.users.some((u) => u.anaUserId === effectiveUserId);

          return (
            <Paper
              key={`day_${dayIndex}_shift_${shiftIdx}`}
              elevation={isTarget ? 8 : 1}
              sx={{
                p: 2,
                mb: 2,
                transition: "all 0.3s ease-in-out",
                bgcolor: isTarget ? "#f0f7ff" : "#f9f9f9",
                borderLeft: isTarget
                  ? "6px solid #0078d4"
                  : "1px solid #e0e0e0",
              }}
            >
              <Typography variant="body2" fontWeight="bold" color="textPrimary">
                {shiftLabel}
              </Typography>
              <Divider sx={{ my: 1 }} />
              {shift.users.map((user, uIdx) => {
                const role = shiftSel[0].anaUserRoleList.find(
                  (r) => r.anaUserRoleId === user.anaUserRoleId
                );
                const name =
                  allUserName.find((a) => a.anaUserId === user.anaUserId)
                    ?.nameSurname || "-";
                const isCurrentUser = user.anaUserId === effectiveUserId;

                // Controllo per richiesta vacante pendente
                const hasVacantRequest = hasPendingVacantRequest(
                  day.day,
                  shift.anaShiftId,
                  user.anaUserRoleId
                );
                const slotIsVacant = isSlotVacant(user);
                const userIsQualified = isUserQualified(user.anaUserRoleId);

                // --- MODALITÀ VACANTI ---
                if (isVacantSelectMode) {
                  // Se c'è già una richiesta pendente, mostra con opzione cancella
                  if (hasVacantRequest) {
                    return (
                      <Box
                        key={`vacant_req_${dayIndex}_${shiftIdx}_${uIdx}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1,
                          my: 0.5,
                          borderRadius: "6px",
                          bgcolor: "#FEF9E7",
                          border: "2px solid #F39C12",
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {role?.roleName}: {t("Richiesta Inviata")}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            onCancelVacantRequest(day.day, shiftIdx, uIdx)
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  }
                  // SE È IL TURNO DELL'UTENTE, MOSTRALO IN VERDE (come nel desktop)
                  if (isCurrentUser) {
                    return (
                      <Box
                        key={`user_${dayIndex}_${shiftIdx}_${uIdx}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          my: 0.3,
                          p: 0.5,
                          borderRadius: "4px",
                          bgcolor: "#ABEBC6", // Verde come nel desktop
                          border: "2px solid transparent",
                          opacity: 0.8,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            width: "100%",
                          }}
                        >
                          <strong>{role?.roleName}:</strong> {name}
                        </Typography>
                      </Box>
                    );
                  }
                  // Verifica se l'utente è già assegnato a QUESTO turno (in qualsiasi ruolo)
                  const isUserAssignedToThisShift = (shift, userId) => {
                    return shift.users.some(u => u.anaUserId === userId && u.anaUserId !== 0);
                  };
                  // Se lo slot è vacante e l'utente è qualificato E NON È UNA DATA PASSATA, mostra come candidabile
                  if (slotIsVacant && userIsQualified && !avoidSelection  && !isUserAssignedToThisShift(shift, effectiveUserId)) {
                    return (
                      <Box
                        key={`vacant_candidate_${dayIndex}_${shiftIdx}_${uIdx}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1,
                          my: 0.5,
                          borderRadius: "6px",
                          bgcolor: "#e3f2fd",
                          border: "2px dashed #1976d2",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: "#bbdefb",
                            transform: "scale(1.01)",
                          },
                        }}
                        onClick={() => {
                          onClickUser(day.day, shiftIdx, uIdx, "");
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary"
                        >
                          {role?.roleName}: {t("Candidati Ora")}
                        </Typography>
                        <PersonAddIcon fontSize="small" color="primary" />
                      </Box>
                    );
                  }

                  // Slot non candidabile
                  let statusText = "";
                  if (!avoidSelection)
                    {
                      if (!slotIsVacant) {
                        statusText = "";//` (${t("assigned")})`;
                      } else if (!userIsQualified) {
                        statusText = ` (${t("Non qualificato")})`;
                      } else if (avoidSelection) {
                        statusText = ""; //` (${t("passed")})`;
                      }
                  }
                 

                  return (
                    <Box
                      key={`vacant_${dayIndex}_${shiftIdx}_${uIdx}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 0.5,
                        my: 0.3,
                        opacity: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{role?.roleName}:</strong> {name}
                        {statusText}
                      </Typography>
                    </Box>
                  );
                }

                // --- RENDERING STANDARD (STESSO COMPORTAMENTO DEL DESKTOP) ---
                return (
                  <Box
                    key={`user_${dayIndex}_${shiftIdx}_${uIdx}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      my: 0.3,
                      p: 0.5,
                      borderRadius: "4px",
                      bgcolor: isCurrentUser ? "#ABEBC6" : "transparent",
                      border: user.isSelected
                        ? "2px solid #006400"
                        : "2px solid transparent",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        bgcolor: isCurrentUser ? "#8fdfb0" : "#f0f0f0",
                      },
                    }}
                    onClick={() => {
                      // Stessa logica del desktop: chiama onClickUser
                      if (!avoidSelection) {
                        onClickUser(day.day, shiftIdx, uIdx, name);
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isCurrentUser ? "bold" : "normal",
                        width: "100%",
                      }}
                    >
                      <strong>{role?.roleName}:</strong> {name}
                    </Typography>

                    {/* Icona per richiesta cambio turno - stessa logica desktop */}
                    {user.showChangeRequest && isCurrentUser && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickShiftRequest(shiftIdx, uIdx, day.day, user);
                        }}
                      >
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Paper>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
};

export default ResponsiveCalendarDay;